/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

/**
 * Removes specified items from the cart.
 *
 * @function removeItemsFromCart
 * @param {Array<number>} variantIds - An array of variant IDs to remove from the cart.
 * @returns {Promise<void>}
 */
async function removeItemsFromCart(variantIds) {
  const updates = variantIds.reduce((acc, id) => {
    acc[id] = 0;
    return acc;
  }, {});

  return fetch("/cart/update.js", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Requested-With": "XMLHttpRequest",
    },
    body: JSON.stringify({ updates }),
  })
    .then((response) => response.json())
    .then(() => {
      setTimeout(() => {
        document.documentElement.dispatchEvent(
          new CustomEvent("cart:refresh", {
            bubbles: true,
          })
        );
      }, 1000);
    })
    .catch((error) => {
      console.error("Failed to remove products from cart:", error);
    });
}

document.addEventListener("cart:updated", function (event) {
  var cart = event.detail.cart;

  const filteredItems = cart.items
    .filter((item) => {
      return item.properties?._BundleParentId;
    })
    .map((item) => {
      return item.id;
    });

  if (filteredItems.length > 0) {
    removeItemsFromCart(filteredItems);
  }
});

if (!customElements.get("swiper-element")) {
  customElements.define(
    "swiper-element",
    class SwiperElement extends HTMLElement {
      constructor() {
        super();
        this.slider = null;
        this.config = JSON.parse(this.getAttribute("swiper-config"));
      }

      connectedCallback() {
        this.init();
      }

      disconnectedCallback() {
        this.slider.destroy(true, true);
      }

      init() {
        this.slider = new Swiper(this, this.config);
      }

      get swiperInstance() {
        return this.slider;
      }
    }
  );
}

if (!customElements.get("content-modal")) {
  customElements.define(
    "content-modal",
    class ContentModal extends HTMLElement {
      constructor() {
        super();
        this.trigger = this.querySelector("div[data-trigger]");
        this.content = this.querySelector("div[data-content]");
      }

      connectedCallback() {
        this.init();
      }

      disconnectedCallback() {}

      init() {
        this.trigger.addEventListener("click", () => {
          if (this.content.hasAttribute("open")) {
            this.content.removeAttribute("open");
            this.trigger.removeAttribute("active");
          } else {
            this.content.setAttribute("open", "");
            this.trigger.setAttribute("active", "");
          }
        });
      }
    }
  );
}

// Shipping Info

function changeShippingInfo(variant) {
  const shippingInfoBox = document.querySelector(".shipping-info__normal")
    .children[0];
  const shippingSpecialInfo = document.querySelector(
    ".shipping-info__special-text"
  );
  const { shippingInfo } = window;

  if (variant.available) {
    shippingInfoBox.textContent = shippingInfo.inStock;
    shippingInfoBox.classList.remove("shipping-info--out-of-stock");
    shippingInfoBox.classList.add("shipping-info--in-stock");

    if (shippingSpecialInfo) {
      shippingSpecialInfo.textContent = shippingInfo.specialInfo;
    }
  } else {
    shippingInfoBox.textContent = shippingInfo.outOfStock;
    shippingInfoBox.classList.remove("shipping-info--in-stock");
    shippingInfoBox.classList.add("shipping-info--out-of-stock");

    if (shippingSpecialInfo) {
      shippingSpecialInfo.textContent = "";
    }
  }
}

document.addEventListener("variant:changed", function (event) {
  let variant = event.detail.variant; // Gives you access to the whole variant details
  changeShippingInfo(variant);
});

const element = document.querySelector(".product-content.anchor");
if (element) {
  let tabWrapper = document.querySelector(".wc-product-tab-wrapper");
  if (tabWrapper) {
    tabWrapper.innerHTML = element.innerHTML;
  }
}

var swiper = new Swiper(".wc-product-thumb.mySwiper", {
  slidesPerView: "4",
  freeMode: true,
  watchSlidesProgress: true,
  mousewheel: true,
  pagination: {
    el: ".swiper-pagination",
    type: "progressbar",
  },
  breakpoints: {
    640: {
      slidesPerView: 4,
    },
    768: {
      direction: "vertical",
      slidesPerView: "auto",
      autoHeight: true,
    },
    1024: {
      direction: "vertical",
      slidesPerView: "auto",
      autoHeight: true,
    },
  },
});
var swiper2 = new Swiper(".wc-product-media.mySwiper2", {
  spaceBetween: 10,
  navigation: {
    nextEl: ".swiper-button-next",
    prevEl: ".swiper-button-prev",
  },
  thumbs: {
    swiper: swiper,
  },
});

document.addEventListener("DOMContentLoaded", function () {
  var reviewmyInterval = setInterval(review_widgetmyTimer, 1000);

  function review_widgetmyTimer() {
    var reviewWidgets = document.querySelectorAll(".jdgm-widget");

    if (reviewWidgets.length > 0) {
      reviewWidgets.forEach(function (widget) {
        var thumbnailLinks = widget.querySelectorAll(
          ".jdgm-row-media .jdgm-gallery-wrapper .jdgm-gallery .jdgm-gallery__thumbnail-link"
        );

        if (thumbnailLinks.length > 0) {
          thumbnailLinks.forEach(function (link) {
            var thumbnail = link.querySelector(".jdgm-gallery__thumbnail");
            if (thumbnail) {
              var dataSrc = thumbnail.getAttribute("data-src");
              if (dataSrc) {
                var urlObj = new URL(dataSrc);
                urlObj.search = ""; // Remove query parameters
                var customUrl = urlObj.toString();
                thumbnail.setAttribute("src", customUrl);
              }
            }
          });
        }
      });
      reviewmyStopFunction();
    }
  }

  function reviewmyStopFunction() {
    //clearInterval(reviewmyInterval);
  }
});

document.addEventListener("DOMContentLoaded", function () {
  // Select all <h4> elements inside '.title-with-submenu'
  const titles = document.querySelectorAll(".title-with-submenu h4");

  titles.forEach(function (title) {
    title.addEventListener("click", function () {
      // Toggle active class on the parent '.title-with-submenu'
      const parent = this.parentElement;
      parent.classList.toggle("active");

      // Find the next sibling menu
      const menu = parent.nextElementSibling;

      if (menu && menu.classList.contains("menu")) {
        if (menu.style.maxHeight) {
          // Collapse the menu
          menu.style.maxHeight = null;
        } else {
          // Expand the menu
          menu.style.maxHeight = menu.scrollHeight + "px";
        }
      }

      // Optional accordion behavior: Close other menus
      const otherTitles = document.querySelectorAll(".title-with-submenu h4");
      otherTitles.forEach(function (otherTitle) {
        const otherParent = otherTitle.parentElement;
        const otherMenu = otherParent.nextElementSibling;

        if (otherTitle !== title) {
          otherParent.classList.remove("active");
          if (otherMenu && otherMenu.classList.contains("menu")) {
            otherMenu.style.maxHeight = null;
          }
        }
      });
    });
  });

  let SwiperTop = new Swiper(".logo-swiper-slider.swiper--top", {
    spaceBetween: 15,
    slidesPerView: 5,
    //centeredSlides: true,
    speed: 2000,
    autoplay: {
      delay: 0,
      disableOnInteraction: false,
    },
    breakpoints: {
      480: {
        spaceBetween: 25,
        slidesPerView: 6,
      },
      640: {
        spaceBetween: 25,
        slidesPerView: 7,
      },
      768: {
        spaceBetween: 30,
        slidesPerView: 8,
      },
      1024: {
        spaceBetween: 30,
        slidesPerView: 10,
      },
      1200: {
        spaceBetween: 45,
        slidesPerView: 14,
      },
      1500: {
        spaceBetween: 45,
        slidesPerView: 15,
      },
    },
    loop: true,
    //allowTouchMove: false,
  });
});
